'use client';

import {Accordion, AccordionGroup, Text, Stack} from '@eds-web/react';
import {css} from '@panda/css';
import {Container} from '../../atoms';
import {useMediaQuery} from '@/hooks';
import {SectionFaqFieldsFragment} from '@common/hygraph-schema';
import {FaqAsset} from './FaqAsset.atom';
import {applySetLangToHtmlAnchors} from '@/lib/utils/applySetLangToHtmlAnchors';
import {useEffect} from 'react';

const maxWidthStyle = css({maxWidth: '100%'});

const accordionStyle = css({
	maxWidth: '100%',
	overflowX: 'auto',
	bg: 'eds.background.elevated.high.default !important',
	'& > div': {
		maxWidth: '100%'
	},
	sm: {
		'& button': {
			paddingX: 'eds.dimension.32 !important',
			paddingY: 'eds.dimension.24 !important',
			'&:hover': {
				backgroundColor: 'inherit !important'
			}
		},
		'& button[aria-expanded="true"]': {
			paddingBottom: 'eds.dimension.16 !important'
		},
		'& > div': {
			paddingX: 'eds.dimension.32 !important',
			paddingBottom: 'eds.dimension.24 !important'
		}
	}
});

export const FAQSection = ({asset, faq}: SectionFaqFieldsFragment) => {
	const isMobile = useMediaQuery('sm');

	// Mimic scroll behavior on page load.
	useEffect(() => {
		if (typeof window !== 'undefined' && window.location.hash) {
			const el = document.getElementById(window.location.hash.slice(1));
			el?.scrollIntoView({behavior: 'smooth'});
		}
	}, []);

	return (
		<Container pb="55px" pt={{base: '80px', smDown: '24px'}}>
			<Stack direction="column" justify="center" gap={isMobile ? 64 : 80}>
				{asset && <FaqAsset {...asset} />}
				{faq && (
					<Stack
						id="faq"
						as="div"
						gap={32}
						justify="start"
						align="stretch"
						direction="column"
						className={`nested-link ${maxWidthStyle}`}>
						{faq.title && (
							<Text
								variant={isMobile ? 'headingMD' : 'headingXL'}
								className={css({
									textAlign: 'center',
									smDown: {
										textAlign: 'left'
									}
								})}>
								{faq.title}
							</Text>
						)}

						{faq.items && (
							<AccordionGroup className={maxWidthStyle}>
								{faq.items
									.filter((item) => item.hidden !== true)
									.map((item, key) => {
										return (
											<Accordion
												key={key}
												className={accordionStyle}
												headerTitle={item.question}
												defaultExpanded={!!item.defaultOpen}>
												{!item.answer ? (
													'-'
												) : (
													<Text
														as="div"
														color={item.answer.color}
														variant={item.answer.textVariant}>
														<Stack
															dangerouslySetInnerHTML={{
																__html: applySetLangToHtmlAnchors(
																	item.answer.html
																)
															}}
														/>
													</Text>
												)}
											</Accordion>
										);
									})}
							</AccordionGroup>
						)}
					</Stack>
				)}
			</Stack>
		</Container>
	);
};
